import { Injectable } from "@angular/core"
import { AngularFireAuth } from "@angular/fire/auth"
import { AngularFirestore, AngularFirestoreDocument } from "@angular/fire/firestore"
import { AngularFireFunctions } from "@angular/fire/functions"
import { Router } from "@angular/router"
import { Company, CompanyUser, Credits, LoginVariable, Project, RegistrationResult, TestRampAuthentication, User } from "@tr/models"
import { showNotification } from "@tr/utils"
import { UserComponent } from "app/shared/users/user/user.component"
import { environment } from "environments/environment"
import * as firebase from "firebase/app"
import { BehaviorSubject, Observable, throwError } from "rxjs"
import { tap } from "rxjs/operators"
import { ApiPaths, ApiService } from "./api.service"
import { CompaniesService } from "./companies.service"
import { DataService } from "./data.service"
import { SessionService } from "./session.service"
import { SwalService } from "./swal.service"

@Injectable({
  providedIn: "root",
})
export class AuthService {
  public uid: string
  public company: string
  public email: string
  public firstName: string
  public lastName: string
  public isCreditLimit: boolean
  public creditLimit: number
  public photoUrl: string
  public roles: string[]
  public totalTestCases: number
  public currentProject: string
  public unsubscribeFromUser: () => void
  public unsubscribeFromCompanyUser: () => void
  public unsubscribeFromProject: () => void

  public testRampAuth: TestRampAuthentication = null

  public firstLoad: boolean

  public currentUser: UserComponent
  public user: Observable<firebase.User>
  private userDetails: firebase.User = null
  public loginAlert: string

  // new
  public redirectUrl: string

  // BehaviorSubjects have an initial value.
  // isLoggedIn is property (not function) now:
  public isLoggedIn = new BehaviorSubject<boolean>(false)

  constructor(
    private sessionService: SessionService,
    private dataService: DataService,
    private apiService: ApiService,
    private companiesService: CompaniesService,
    private firebaseAuth: AngularFireAuth,
    private afs: AngularFirestore,
    private router: Router,
    private functions: AngularFireFunctions,
  ) {
    this.user = firebaseAuth.authState
    this.roles = []
    this.firstLoad = false
    this.retrieveSessionData()
  }

  // Asks database for the user info (name, email, etc.)
  // and client company data including credits, test case count, etc.
  public async retrieveDatabaseInfo(user?: User, companyUser?: CompanyUser, project?: Project, credits?: Credits, useDefaultTestCount = false): Promise<void> {
    if (!this.user) { return }

    if (!user) {
      user = await this.getUserData()
    }

    this.roles = user.roles
    this.company = user.company

    this.dataService.roles.next(user.roles)
    this.dataService.company.next(user.company)

    await Promise.all([
      this.getCredits(credits),
      this.getProjectData(project),
      this.getCurrentTestCount(useDefaultTestCount),
      this.getCompanyUserData(companyUser),
    ])

    this.storeSessionData()
    this.subscribeToUserData()
    await this.navigateToDashboard(this.roles)
  }

  public async getUserData(): Promise<User> {
    const user = await this.apiService.get(`${ApiPaths.users}/${this.uid}`)

    if (!user) {
      return null
    }

    return {
      ...user as User,
      _id: user.uid,
    }
  }

  public async getProjectData(project?: Project): Promise<void> {
    if (!project) {
      if (!this.currentProject) {
        return
      }

      const projectSnapshot = await this.afs
        .collection("companies")
        .doc(this.company)
        .collection("projects")
        .doc(this.currentProject)
        .ref.get()
      project = projectSnapshot.data() as Project
      if (!projectSnapshot.exists) { return }
    }

    this.updateProjectData(project)
  }

  public updateProjectData(project: Project): void {
    if (!project) { return }
    this.totalTestCases = project.totalTestCases
    this.dataService.totalTestCases.next(project.totalTestCases)
    this.dataService.currentTestCount.next(project.currentTestCount)
    this.dataService.lastRunId.next(project.lastRunId)
    this.dataService.totalBugs.next(project.totalBugs)
  }

  public async getCredits(credits?: Credits): Promise<void> {
    if (!this.company && !credits) { return }

    if (!credits) {
      const docCredits = await this.afs
        .collection("companies")
        .doc(this.company)
        .collection("credits")
        .doc("credits")
        .ref.get()

      if (!docCredits.exists) { return }
      credits = docCredits.data() as Credits
    }

    this.dataService.creditsRemaining.next(credits.total)
  }

  public async getTestRunData(): Promise<void> {
    if (!this.company || !this.currentProject) { return }

    const testRunsSnapshot = await this.afs
      .collection("companies")
      .doc(this.company)
      .collection("projects")
      .doc(this.currentProject)
      .collection("Test Runs")
      .ref.get()

    let passedCount = 0
    let failedCount = 0
    let incompleteCount = 0
    let blockedCount = 0
    testRunsSnapshot.forEach(doc1 => {
      passedCount += doc1.data().passed
      failedCount += doc1.data().failed
      if (doc1.data().incomplete) {
        incompleteCount += doc1.data().incomplete
      }
      if (doc1.data().blocked) {
        blockedCount += doc1.data().blocked
      }
    })

    this.dataService.totalPassed.next(passedCount)
    this.dataService.totalFailed.next(failedCount)
    this.dataService.totalIncomplete.next(incompleteCount)
    this.dataService.totalBlocked.next(blockedCount)
  }

  public async getTotalBugs(): Promise<void> {
    if (!this.company || !this.currentProject) { return }

    const bugsSnapshot = await this.afs
      .collection("companies")
      .doc(this.company)
      .collection("projects")
      .doc(this.currentProject)
      .collection("Bugs")
      .ref.get()

    const totalBugs = bugsSnapshot.size
    // TODO: This will be fixed before releasing to prod
    // await this.afs
    //   .collection("companies")
    //   .doc(this.company)
    //   .collection("projects")
    //   .doc(this.currentProject)
    //   .update({ totalBugs })
    this.dataService.totalBugs.next(totalBugs)
  }

  public async getCurrentTestCount(useDefaultTestCount: boolean = false): Promise<void> {
    if (!this.company || !this.currentProject) { return }

    if (useDefaultTestCount) {
      this.dataService.currentTestCount.next(0)
      return
    }

    if (this.currentProject && this.currentProject.length > 0) {
      const testCasesSnapshot = await this.afs
        .collection("companies")
        .doc(this.company)
        .collection("projects")
        .doc(this.currentProject)
        .collection("Test Cases")
        .ref.get()
      this.dataService.currentTestCount.next(testCasesSnapshot.size)
    }
  }

  public async getCompanyUserData(companyUser?: CompanyUser): Promise<void> {
    if (!companyUser) {
      companyUser = await this.companiesService.getCompanyUser(
        this.company,
        this.uid,
      )
    }

    this.updateCompanyUserData(companyUser)
  }

  public updateCompanyUserData(companyUser: CompanyUser): void {
    this.firstName = companyUser.firstName
    this.lastName = companyUser.lastName
    this.isCreditLimit = companyUser.isCreditLimit
    this.creditLimit = companyUser.creditLimit
    this.photoUrl = companyUser.photoUrl
    this.email = companyUser.email
    this.dataService.email.next(companyUser.email)
    this.dataService.firstName.next(companyUser.firstName)
    this.dataService.lastName.next(companyUser.lastName)
    this.dataService.isCreditLimit.next(companyUser.isCreditLimit)
    this.dataService.creditLimit.next(companyUser.creditLimit)
    this.dataService.photoUrl.next(companyUser.photoUrl)

    if (!this.currentProject) {
      this.currentProject = companyUser.defaultProject
      this.dataService.project.next(this.currentProject)
    }
  }

  /**
   * Navigates to the proper dashboard based on the roles of the user
   */
  public async navigateToDashboard(roles: string[]): Promise<void> {
    if (
      this.router.url.includes("/pages/login") ||
      this.router.url.includes("/pages/register")
    ) {
      this.firstLoad = true
      if (roles.includes("Admin")) {
        await this.router.navigate(["dashboard"])
      } else if (
        roles.includes("SeniorManager") ||
        roles.includes("PartnerSeniorManager")
      ) {
        await this.router.navigate(["captain/dashboard"])
      } else if (
        roles.includes("Manager") ||
        roles.includes("PartnerManager")
      ) {
        await this.router.navigate(["captain/dashboard"])
      } else if (this.roles.includes("SuperUser")) {
        await this.router.navigate(["super/dashboard"])
      } else if (
        roles.includes("Tester") ||
        roles.includes("PartnerTester")
      ) {
        await this.router.navigate(["tester/dashboard"])
      } else if (roles.includes("AccountManager")) {
        await this.router.navigate(["account/dashboard"])
      } else {
        await this.router.navigate(["dashboard"])
      }
    }
  }

  // store user and company details for session to avoid losing on refresh or redirect

  public storeSessionData(): void {
    this.sessionService.setSettings({
      uid: this.dataService.uid.value,
      email: this.dataService.email.value,
      company: this.dataService.company.value,
      roles: this.dataService.roles.value,
      project: this.dataService.project.value,
    })

    this.sessionService.setItem<TestRampAuthentication>(
      this.sessionService.testRampAuthName,
      this.testRampAuth,
    )
  }

  // retrieve the stored session data, called on reload

  public async retrieveSessionData(): Promise<void> {
    this.testRampAuth = this.sessionService.getItem(this.sessionService.testRampAuthName)
    const sessionUser = this.sessionService.getUserSettings()

    if (sessionUser != null && this.user) {
      this.uid = sessionUser.uid
      this.email = sessionUser.email
      this.company = sessionUser.company
      this.currentProject = sessionUser.project
      this.roles = sessionUser.roles

      this.dataService.uid.next(this.uid)
      this.dataService.email.next(this.email)
      this.dataService.company.next(this.company)
      this.dataService.project.next(this.currentProject)
      this.dataService.roles.next(this.roles)

      try {
        await Promise.all([
          this.getCredits(),
          this.getCurrentTestCount(),
          this.getTotalBugs(),
          this.getTestRunData(),
        ])

        this.subscribeToUserData()

      } catch (error) {
        console.error(error)
        showNotification(error, "warning")
      }
    }
  }

  public async signUpUserFromAdmin(
    firstName: string,
    lastName: string,
    password: string,
    company: string,
    email: string,
    project: string,
    roles: string[],
  ): Promise<void> {
    try {
      const userCredential: any = await this.firebaseAuth.auth
        .createUserWithEmailAndPassword(email, password)
      await this.createUser(userCredential.user.uid, firstName, lastName, company, email, roles)
      await this.createCompanyUserFromApi(userCredential.user, firstName, lastName, company, project, email)
    } catch (err) {
      console.error(err)
      await SwalService.showModal(`User Creation Error: ${err}`)
    }
  }

  public async createUser(
    uid: string,
    firstName: string,
    lastName: string,
    company: string,
    email: string,
    roles: string[],
  ): Promise<User> {
    try {
      return this.apiService.post<User>(ApiPaths.users, {
        name: `${firstName} ${lastName}`,
        company,
        email,
        uid,
        roles,
      })
    } catch (err) {
      console.error(err)
    }
  }

  public async createCompanyUserFromApi(
    user: firebase.User,
    firstName: string,
    lastName: string,
    company: string,
    project: string,
    email: string,
  ): Promise<any> {
    return await this.apiService.post(
      ApiPaths.companyUser(company),
      this.getCompanyUserObject(user, firstName, lastName, company, project, email),
    )
  }

  public getCompanyUserObject(
    user: firebase.User,
    firstName: string,
    lastName: string,
    company: string,
    project: string,
    email: string,
  ): CompanyUser {
    return {
      uid: user.uid,
      name: `${firstName} ${lastName}`,
      firstName,
      lastName,
      defaultProject: project,
      company,
      email,
      lastRun: new Date("January 1, 2000"),
      isCreditLimit: false,
      creditLimit: 200000,
      about: "",
      creditCount: 0,
      testsRunCount: 0,
      loginMethod: "email",
    }
  }

  public getCompanyDoc(company: string): AngularFirestoreDocument<Company> {
    return this.afs
      .collection("companies")
      .doc(company)
  }

  public async signupEmail(
    email: string,
    password: string,
    firstName: string,
    lastName: string,
    company: string,
    project: string,
    websiteUrl: string,
    agreementDateAndTime: firebase.firestore.FieldValue,
    routeToNavigateTo = "dashboard",
  ): Promise<{ error?: string }> {
    try {
      const companyExists = await this.companiesService.companyExists(company)
      if (companyExists) {
        return {
          error: "This company already exists. Please ask your admin to add you as a user.",
        }
      }

      const userCredential = await this.firebaseAuth.auth
        .createUserWithEmailAndPassword(email, password)

      const firebaseUser = userCredential.user

      const registerUser = this.functions.httpsCallable("registerUser")
      const userRegistered = await registerUser({
        email: firebaseUser.email,
        uid: firebaseUser.uid,
        firstName,
        lastName,
        company,
        project,
        websiteUrl,
        agreementDateAndTime,
      }).toPromise()

      const registrationInfo = userRegistered as RegistrationResult

      this.dataService.clearData()

      this.company = company
      this.currentProject = project
      this.uid = firebaseUser.uid
      this.email = firebaseUser.email
      this.firstName = firstName
      this.lastName = lastName

      this.dataService.company.next(company)
      this.dataService.project.next(project)
      this.dataService.projectData.next(registrationInfo.projectData)
      this.dataService.uid.next(firebaseUser.uid)
      this.dataService.email.next(firebaseUser.email)
      this.dataService.firstName.next(firstName)
      this.dataService.lastName.next(lastName)
      this.dataService.displayName.next(firebaseUser.displayName)
      this.dataService.photoUrl.next(firebaseUser.photoURL)

      await this.authenticateWithApi(firebaseUser.uid)
      await this.retrieveDatabaseInfo(
        registrationInfo.user,
        registrationInfo.companyUser,
        registrationInfo.projectData,
        registrationInfo.credits,
        true,
      )

      return {}
    } catch (error) {
      console.error(error)
      const errorMessage = `Registration Error: ${error.message}`
      return { error: errorMessage }
    }
  }

  public async login(email: string, password: string): Promise<{ error?: string }> {
    try {
      const userCredentials = await this.firebaseAuth.auth
        .signInWithEmailAndPassword(email, password)

      const value = userCredentials.user
      this.uid = value.uid
      this.email = value.email

      this.dataService.uid.next(value.uid)
      this.dataService.email.next(value.email)

      await this.authenticateWithApi(value.uid)
      await this.retrieveDatabaseInfo()

      return {}

    } catch (err) {
      console.error(err)
      const message = "Something went wrong:" + err.message
      return { error: message }
    }
  }

  public subscribeToUserData(): void {
    if (this.uid && !this.unsubscribeFromUser) {
      this.unsubscribeFromUser = this.afs
        .collection("users")
        .doc<User>(this.uid)
        .ref.onSnapshot(userSnapshot => {
          if (!userSnapshot.exists) { return }

          const user = userSnapshot.data() as User
          this.roles = user.roles
          this.dataService.roles.next(this.roles)
          this.dataService.email.next(user.email)
          this.dataService.uid.next(user.uid)
          this.storeSessionData()
        })
    }

    if (this.uid && this.company && !this.unsubscribeFromCompanyUser) {
      this.unsubscribeFromUser = this.afs
        .collection("companies")
        .doc(this.company)
        .collection("users")
        .doc(this.uid)
        .ref.onSnapshot(async companyUserSnapshot => {
          if (!companyUserSnapshot.exists) { return }

          const companyUser = companyUserSnapshot.data() as CompanyUser

          this.uid = companyUser.uid
          this.email = companyUser.email
          this.firstName = companyUser.firstName
          this.lastName = companyUser.lastName
          this.photoUrl = companyUser.photoUrl
          this.company = companyUser.company
          this.currentProject = companyUser.defaultProject || this.currentProject

          this.isCreditLimit = companyUser.isCreditLimit
          this.creditLimit = companyUser.creditLimit

          this.dataService.uid.next(this.uid)
          this.dataService.email.next(this.email)
          this.dataService.firstName.next(companyUser.firstName)
          this.dataService.lastName.next(companyUser.lastName)
          this.dataService.company.next(this.company)
          this.dataService.project.next(this.currentProject)
          this.dataService.photoUrl.next(companyUser.photoUrl)
          this.dataService.isCreditLimit.next(companyUser.isCreditLimit)
          this.dataService.creditLimit.next(companyUser.creditLimit)
          this.storeSessionData()
          await this.dataService.changeProjectAndResubscribe(this.currentProject, this.storeSessionData.bind(this))
        })
    }
  }

  /**
   * @function authenticateWithApi$
   * Received an auth token from the TestRamp API and stores for later use.
   *
   * @param uid The logged-in user's id
   * @param tries Ignore this parameter as it is used internally
   */
  public authenticateWithApi$(uid: string, tries = 0): Observable<TestRampAuthentication> {
    try {
      return this.apiService
        .post$<any, TestRampAuthentication>(
          ApiPaths.authToken, {}, {}, { ...environment.api.auth, uid },
        )
        .pipe(
          tap(testRampAuth => {
            this.testRampAuth = testRampAuth
            this.sessionService.setItem(this.sessionService.testRampAuthName, this.testRampAuth)
          }),
        )
    } catch (err) {
      console.error(err)
      const unknown404Error = "Cannot POST /auth/token"
      if (tries < 3 && err.error && (err.error as string).toLowerCase().indexOf(unknown404Error) >= 0) {
        return this.authenticateWithApi$(uid, tries + 1)
      }

      return throwError(err)
    }
  }

  /**
   * @function authenticateWithApi
   * Received an auth token from the TestRamp API and stores for later use.
   *
   * @param uid The logged-in user's id
   * @param tries Ignore this parameter as it is used internally
   */
  public async authenticateWithApi(uid: string): Promise<TestRampAuthentication> {
    try {
      const testRampAuth = await this.apiService
        .post<any, TestRampAuthentication>(
          ApiPaths.authToken, {}, {}, { ...environment.api.auth, uid },
        )
      this.testRampAuth = testRampAuth
      this.sessionService.setItem(this.sessionService.testRampAuthName, this.testRampAuth)
      return testRampAuth
    } catch (err) {
      throw err
    }
  }

  public authenticateWithRefreshToken(err: any): Observable<TestRampAuthentication> {
    this.testRampAuth = this.sessionService.getItem(this.sessionService.testRampAuthName)
    if (this.testRampAuth && this.testRampAuth.refreshToken) {
      return this.apiService
        .post$<any, TestRampAuthentication>(
          ApiPaths.authTokenRefresh,
          {}, {},
          { client_id: environment.api.auth.client_id, refresh_token: this.testRampAuth.refreshToken },
        )
        .pipe(
          tap(testRampAuth => {
            this.testRampAuth = testRampAuth
            this.sessionService.setItem(this.sessionService.testRampAuthName, this.testRampAuth)
          }),
        )
    } else {
      if (this.uid && this.isLoggedIn) {
        return this.authenticateWithApi$(this.uid)
      }
      return new Observable(observer => {
        try {
          throw Error("Could not retrieve refresh token. Needing to login again.")
        } catch (errLoggingOut) {
          observer.error(err)
        }
      })

    }
  }

  public getAuthHeader(): string {
    this.testRampAuth = this.testRampAuth
      ? this.sessionService.getItem(this.sessionService.testRampAuthName)
      : this.testRampAuth

    if (!this.testRampAuth) {
      return ""
    }
    return `${this.testRampAuth.tokenType} ${this.testRampAuth.token}`
  }

  public clearTestRampAuthSession(): void {
    this.sessionService.setItem(this.sessionService.testRampAuthName, null)
  }

  public async logout(): Promise<void> {
    await this.firebaseAuth.auth.signOut()

    if (this.unsubscribeFromUser) {
      this.unsubscribeFromUser()
      this.unsubscribeFromUser = null
    }

    if (this.unsubscribeFromCompanyUser) {
      this.unsubscribeFromCompanyUser()
      this.unsubscribeFromCompanyUser = null
    }

    if (this.unsubscribeFromProject) {
      this.unsubscribeFromProject()
      this.unsubscribeFromProject = null
    }

    this.sessionService.cleanAll()
    this.dataService.clearData()
  }

  public async resetPassword(email: string): Promise<void> {
    try {
      await firebase.auth().sendPasswordResetEmail(email)
      showNotification("Password Reset Email Sent", "success")
    } catch (error) {
      showNotification(error, "warning")
    }
  }

  public async getTestRunCredentials(loginTokens: string[]): Promise<LoginVariable[]> {
    const getTestRunCredentials = this.functions.httpsCallable("getTestRunCredentials")
    return getTestRunCredentials(loginTokens).toPromise()
  }

  public async getLoginTokens(loginVariables: LoginVariable[]): Promise<string[]> {
    const getTestRunTokens = this.functions.httpsCallable("getTestRunTokens")
    return getTestRunTokens(loginVariables).toPromise()
  }

  public async enableDisableCompanyUser(uid: string, email: string, disabled: boolean): Promise<void> {
    const enableDisableUser = this.functions.httpsCallable("enableDisableUser")
    await enableDisableUser({ email, disabled }).toPromise()
  }

  // updates user email that is stored in database, does not change the email user logs in with
  public async userInfoUpdated(): Promise<void> {
    const user = firebase.auth().currentUser
    return firebase
      .database()
      .ref("users/" + user.uid)
      .set({
        email: user.email,
      })
  }
}
